import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { useToast } from "@/shared/rd";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useReissueSellerInviteMutation } from "../services/useReissueSellerInviteMutation";

export type ReissuedInvite = { inviteToken: string; expiresAt: string };

/**
 * Reissues the seller's invite; the token panel shows what came back, and a
 * short toast names who it was sent to. A failure keeps the server's detail
 * ("" when it gave none) for the panel's error Banner.
 */
export function useInviteReissue(sellerId: string, sellerName: string) {
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<ReissuedInvite | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: reissueInvite } = useReissueSellerInviteMutation();

  const reissue = async () => {
    setError(null);
    setInvite(null);
    setIsSubmitting(true);
    try {
      const payload = await reissueInvite({ sellerId });
      setInvite({ inviteToken: payload.inviteToken, expiresAt: payload.expiresAt });
      toast.show({ message: t("admin.inviteReissuedToast", { name: sellerName }) });
    } catch (caught) {
      setError(caught instanceof ApiJsonClientError ? caught.message || "" : "");
    } finally {
      setIsSubmitting(false);
    }
  };

  return { invite, error, isSubmitting, reissue };
}
